// iter150AZ.112 — DR404 frame builder + CRC16 Modbus.
//
// Pure functions only — no DOM / Web Serial dependencies, so unit
// tests and the backend's mirror validator can stay byte-compatible.
//
// Frame layout per the user's spec:
//   * Register  frame: F841 + <FULL_MAC_HEX> + <CRC_LO><CRC_HI>
//   * Heartbeat frame: F842 + <FULL_MAC_HEX> + <CRC_LO><CRC_HI>
//
// Total frame size = 1 (slave) + 1 (fn) + 6 (MAC) + 2 (CRC)
//                  = 10 bytes / 20 hex chars.

const _HEX_RE = /^[0-9a-fA-F]+$/;

export const FRAME_HEX_LENGTH      = 20;        // 10 bytes total
export const REGISTER_FN_CODE      = 0x41;
export const HEARTBEAT_FN_CODE     = 0x42;
export const SLAVE_ADDR            = 0xF8;


/**
 * Strip separators (`:`, `-`, ` `) from a MAC string and uppercase.
 * Returns an empty string for invalid input so the caller can branch
 * on falsy. ``"D4-AD-20-82-72-68"`` → ``"D4AD20827268"``.
 */
export function normalizeMac(raw) {
  if (raw == null) return "";
  const s = String(raw).replace(/[\s:\-_.]/g, "").toUpperCase();
  if (s.length !== 12 || !_HEX_RE.test(s)) return "";
  return s;
}


/**
 * Last four hex characters of the MAC. Used as the "MAC suffix" that
 * personalises the AP SSID (`ACC WiFi 7268`) and DHCP hostname.
 */
export function macSuffix4(macHex) {
  const s = normalizeMac(macHex);
  return s ? s.slice(-4) : "";
}


/**
 * Modbus RTU CRC16. Polynomial 0xA001 (reversed 0x8005), seed 0xFFFF.
 * Returns a 16-bit unsigned int. The caller appends ``crc & 0xFF``
 * first, then ``(crc >> 8) & 0xFF`` to match Modbus byte order.
 */
export function crc16Modbus(bytes) {
  let crc = 0xFFFF;
  for (let i = 0; i < bytes.length; i++) {
    crc ^= bytes[i] & 0xFF;
    for (let bit = 0; bit < 8; bit++) {
      if (crc & 1) {
        crc = (crc >>> 1) ^ 0xA001;
      } else {
        crc = crc >>> 1;
      }
    }
  }
  return crc & 0xFFFF;
}


/**
 * Convert a hex string (even length, hex chars only) to a byte array.
 * Throws on malformed input.
 */
export function hexToBytes(hex) {
  const s = String(hex || "").trim();
  if (!s || s.length % 2 !== 0 || !_HEX_RE.test(s)) {
    throw new Error(`bad hex string: ${s.slice(0, 24)}`);
  }
  const out = new Uint8Array(s.length / 2);
  for (let i = 0; i < s.length; i += 2) {
    out[i / 2] = parseInt(s.substr(i, 2), 16);
  }
  return out;
}


/**
 * Convert a byte array to an upper-case hex string with no separators.
 */
export function bytesToHex(bytes) {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, "0").toUpperCase();
  }
  return out;
}


/**
 * Internal — build a full F8 / fn / MAC / CRC frame.
 */
function _buildFrame(fnCode, macHex) {
  const mac = normalizeMac(macHex);
  if (!mac) throw new Error("invalid MAC; expected 12 hex chars");

  const macBytes = hexToBytes(mac);
  const payload = new Uint8Array(2 + macBytes.length);
  payload[0] = SLAVE_ADDR;
  payload[1] = fnCode;
  payload.set(macBytes, 2);

  const crc = crc16Modbus(payload);
  const crcLo = crc & 0xFF;
  const crcHi = (crc >> 8) & 0xFF;

  const full = new Uint8Array(payload.length + 2);
  full.set(payload, 0);
  full[full.length - 2] = crcLo;
  full[full.length - 1] = crcHi;
  return bytesToHex(full);
}


/**
 * Build the register frame for a given DR404 MAC. Matches the spec:
 * ``F841 + <FULL_MAC_HEX> + <CRC_LO><CRC_HI>`` (20 hex chars).
 */
export function buildRegisterFrame(macHex) {
  return _buildFrame(REGISTER_FN_CODE, macHex);
}


/**
 * Build the heartbeat frame for a given DR404 MAC. Matches the spec:
 * ``F842 + <FULL_MAC_HEX> + <CRC_LO><CRC_HI>`` (20 hex chars).
 */
export function buildHeartbeatFrame(macHex) {
  return _buildFrame(HEARTBEAT_FN_CODE, macHex);
}


/**
 * Validate that a frame matches the expected DR404 layout AND the
 * embedded CRC verifies. Returns ``{ok, reason, mac}``.
 *
 *   * ``expectedFn`` — 0x41 or 0x42 (register or heartbeat).
 *   * ``expectedMac`` — optional; when given, the embedded MAC must
 *     match (case-insensitive, separators stripped).
 */
export function validateFrame(hex, expectedFn, expectedMac) {
  const s = String(hex || "").toUpperCase();
  if (!_HEX_RE.test(s))                return { ok: false, reason: "not hex" };
  if (s.length !== FRAME_HEX_LENGTH)   return { ok: false, reason: `frame length must be ${FRAME_HEX_LENGTH} hex chars (10 bytes)` };

  const bytes  = hexToBytes(s);
  if (bytes[0] !== SLAVE_ADDR) {
    return { ok: false, reason: `bad slave addr 0x${bytes[0].toString(16)} (expected 0xF8)` };
  }
  if (typeof expectedFn === "number" && bytes[1] !== expectedFn) {
    return { ok: false, reason: `bad function code 0x${bytes[1].toString(16)} (expected 0x${expectedFn.toString(16)})` };
  }

  // CRC verify — recompute over the first 8 bytes and compare.
  const payload = bytes.subarray(0, 8);
  const wantCrc = crc16Modbus(payload);
  const gotCrc  = bytes[8] | (bytes[9] << 8);
  if (wantCrc !== gotCrc) {
    return {
      ok: false,
      reason: `bad CRC ${gotCrc.toString(16).padStart(4, "0").toUpperCase()} `
              + `(expected ${wantCrc.toString(16).padStart(4, "0").toUpperCase()})`,
    };
  }

  // MAC bytes (positions 2..7).
  const macHex = bytesToHex(bytes.subarray(2, 8));
  if (expectedMac) {
    const want = normalizeMac(expectedMac);
    if (want && want !== macHex) {
      return { ok: false, reason: `MAC mismatch: frame=${macHex} expected=${want}` };
    }
  }

  return { ok: true, mac: macHex };
}


/**
 * AP SSID derived from a DR404 MAC: ``"ACC WiFi <last4>"``.
 * Empty MAC → empty string so the caller can branch.
 */
export function apSsidFromMac(macHex) {
  const suffix = macSuffix4(macHex);
  return suffix ? `ACC WiFi ${suffix}` : "";
}
